import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

import { makeArray } from '../../../../shared/utils/makeArray';
import { ProductProvisioningContextService as service } from './ProductProvisioningContextService';
import type { FeatureLookup, FeatureOptions, ProductProvisioning, ProviderProps } from './types/productProvisioning.types';

/*
  Future improvements

  vidaliaOnly?: boolean;
    if true, the feature is only available to vidalia users when provisioned via launch darkly, false otherwise
    needed for vidalia only features (none currently)
  exclude?: string[];
    if provided, the feature is not available if its provisioned and provided string in the array
    needed for some vidalia features (none currently)
  enterprise?: boolean;
    if true, the feature is available to enterprise customers
    needed for HtmlCssSingleTabSwitch and other enterprise features
*/

// Internal use only
type InternalFeatureProvision = {
  name: string;
  flipperFlag?: string | string[];
  legacyProductFlag?: string | string[];
  darklyProductFlag?: string | string[];
  subscription?: string | string[];
  limit?: number;
  included?: string[];
  legacyAccess?: boolean;
  vidaliaOnly?: boolean;
};

export const ProductProvisioningContext = createContext<ProductProvisioning | undefined>(undefined);

export const ProductProvisioningProvider = (props: ProviderProps) => {
  const {
    children,
    isVidaliaEnabled,
    flipperFlags,
    legacyProductFlags,
    launchDarklyFlags,
    subscriptions,
    featureBypass,
    features,
  } = props;

  const [featureLookup, setFeatureLookup] = useState<FeatureLookup>({});
  const [cacheId, setCacheId] = useState<string>('');

  const updateLookup = useCallback((updatedProps: ProviderProps) => {
    const lookup: FeatureLookup = {};

    updatedProps.features.forEach((feature) => {
      const {
        name,
        flipperFlag,
        legacyProductFlag,
        darklyProductFlag,
        subscription,
        limit,
        included,
        legacyAccess,
        vidaliaOnly,
      } = feature as InternalFeatureProvision;

      if (updatedProps.featureBypass?.[name]) {
        lookup[name] = true;
        return;
      }

      const configOrTrue = service.buildFeatureConfig(name, { limit, included }) || true;

      if (vidaliaOnly && !updatedProps.isVidaliaEnabled) {
        lookup[name] = false;
        return;
      }

      if (legacyAccess && !updatedProps.isVidaliaEnabled) {
        lookup[name] = true;
        return;
      }

      if (updatedProps.isVidaliaEnabled) {
        lookup[name] = service.checkFlag(darklyProductFlag, updatedProps.launchDarklyFlags) ? configOrTrue : false;
        return;
      }

      lookup[name] =
        service.checkFlag(darklyProductFlag, updatedProps.launchDarklyFlags) ||
        service.checkFlag(flipperFlag, updatedProps.flipperFlags) ||
        service.checkFlag(legacyProductFlag, updatedProps.legacyProductFlags) ||
        service.checkFlag(subscription, updatedProps.subscriptions)
          ? configOrTrue
          : false;
    });

    return setFeatureLookup(lookup);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignores changes to cacheId as this is the only function that sets it
  useEffect(() => {
    const newCacheId = JSON.stringify({
      isVidaliaEnabled,
      flipperFlags,
      legacyProductFlags,
      launchDarklyFlags,
      subscriptions,
      featureBypass,
      features,
    });

    if (newCacheId !== cacheId) {
      setCacheId(newCacheId);
      updateLookup(props);
    }
  }, [isVidaliaEnabled, flipperFlags, legacyProductFlags, launchDarklyFlags, subscriptions, featureBypass, features]);

  const isEnabled = useCallback(
    (feature?: string | string[], options?: FeatureOptions) => {
      if (!feature) return true;
      return makeArray(feature).some((flag) => {
        const flagOrConfig = featureLookup[flag];
        if (typeof flagOrConfig !== 'object') return !!flagOrConfig;

        const { limit, exclude, included } = flagOrConfig;
        const belowLimit = service.isBelowLimit(limit, options?.current);
        const isExcluded = service.isExcluded(exclude, options?.requested);
        const isIncluded = service.isIncluded(included, options?.requested);

        return belowLimit && !isExcluded && isIncluded;
      });
    },
    [featureLookup]
  );

  const getLimit = useCallback(
    (feature?: string | string[], options?: FeatureOptions) => {
      if (!feature) return undefined;

      const enabledOrLimit = makeArray(feature)
        .map((flag) => {
          const flagOrConfig = featureLookup[flag];
          if (typeof flagOrConfig !== 'object') return !!flagOrConfig;

          const { limit, exclude, included } = flagOrConfig;
          const isExcluded = service.isExcluded(exclude, options?.requested);
          const isIncluded = service.isIncluded(included, options?.requested);

          if (!limit) return !isExcluded && isIncluded;

          return !isExcluded && isIncluded ? limit : false;
        })
        .filter((value) => value !== false);

      // App doesn't have access to the feature
      if (enabledOrLimit.length === 0) return 0;

      // No limit is set
      const isUnlimited = enabledOrLimit.includes(true);
      if (isUnlimited) return undefined;

      // If isn't unlimited enabledOrLimit is an array of numbers
      // Find the highest limit
      return (enabledOrLimit as number[]).reduce((groupedLimit, limit) => {
        return Math.max(groupedLimit, limit);
      }, 0);
    },
    [featureLookup]
  );

  const contextValue = useMemo(
    () => ({
      isEnabled: isEnabled,
      getLimit: getLimit,
      isVidaliaEnabled: isVidaliaEnabled,
    }),
    [isEnabled, isVidaliaEnabled, getLimit]
  );

  return <ProductProvisioningContext.Provider value={contextValue}>{children}</ProductProvisioningContext.Provider>;
};

export const useProductProvisioning = () => {
  const context = useContext(ProductProvisioningContext);
  if (context === undefined) {
    throw new Error('useProductProvisioning must be used within a ProductProvisioningProvider');
  }
  return context;
};
